import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Layout } from "react-grid-layout";
import { PageComponent } from "../types/components";
import {
  findComponentDeep,
  updateComponentDeep,
  removeComponentDeep,
  addChildToContainer,
  moveComponentIntoContainer,
  moveComponentToRoot,
} from "../lib/component-tree";
import PageGrid from "../components/PageGrid";
import ComponentPalette from "../components/builder/ComponentPalette";
import ComponentPreview from "../components/builder/ComponentPreview";
import ComponentEditModal from "../components/builder/ComponentEditModal";
import FormModal from "../components/builder/FormModal";
import DataEditModal from "../components/builder/DataEditModal";
import ColumnConfigModal from "../components/builder/ColumnConfigModal";
import ChartFieldsModal from "../components/builder/ChartFieldsModal";
import KanbanColumnsModal from "../components/builder/KanbanColumnsModal";
import PageTemplateModal from "../components/builder/PageTemplateModal";
import { Save, X, Lock, Unlock } from "lucide-react";
import { pageApi } from "../services/page.service";
import { integrationsApi } from "../services/integrations.service";
import { useIntegrationsStore } from "../stores/integrations.store";
import { useWorkspaceStore } from "../stores/workspace.store";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { IconRenderer, ICON_MAP } from "../lib/icon-map";
import { useContainerWidth } from "../hooks/use-container-width";
import { getTemplateById } from "../lib/page-templates";
import { useWorkspaceRole } from "../hooks/use-workspace-role";
import { useAuthStore } from "../stores/auth.store";

export default function PageBuilder() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentWorkspace } = useWorkspaceStore();
  const { canManageStructure } = useWorkspaceRole();
  const isSuperAdmin = useAuthStore((s) => s.user?.isSuperAdmin ?? false);
  const { setIntegrations: setIntegrationsStore } = useIntegrationsStore();
  const { containerRef, width, height } = useContainerWidth();
  const [pageName, setPageName] = useState("");
  const [pageSlug, setPageSlug] = useState("");
  const [pageDescription, setPageDescription] = useState("");
  const [pageIcon, setPageIcon] = useState("📄");
  const [isLocked, setIsLocked] = useState(false);
  const [components, setComponents] = useState<PageComponent[]>([]);
  const [allWorkspaceComponents, setAllWorkspaceComponents] = useState<
    PageComponent[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [editingComponent, setEditingComponent] =
    useState<PageComponent | null>(null);
  const [activeFormComponentId, setActiveFormComponentId] = useState<
    string | null
  >(null);
  const [dataEditingComponentId, setDataEditingComponentId] = useState<
    string | null
  >(null);
  const [columnsEditingComponentId, setColumnsEditingComponentId] = useState<
    string | null
  >(null);
  const [chartFieldsEditingComponentId, setChartFieldsEditingComponentId] =
    useState<string | null>(null);
  const [kanbanColumnsEditingComponentId, setKanbanColumnsEditingComponentId] =
    useState<string | null>(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  useEffect(() => {
    if (!canManageStructure) {
      navigate(slug && slug !== 'new' ? `/pages/${slug}` : '/');
    }
  }, [canManageStructure]);

  useEffect(() => {
    if (currentWorkspace?.id) {
      loadAllWorkspacePages();
    }
  }, [currentWorkspace]);

  useEffect(() => {
    if (!slug || slug === "new") {
      // Verificar se há um templateId no state da navegação
      const state = location.state as { templateId?: string } | null;
      if (state?.templateId) {
        // Aplicar o template automaticamente
        const templateComponents = getTemplateById(state.templateId);
        setComponents(templateComponents);
        setShowTemplateModal(false);
        // Limpar o state para evitar reaplicar o template
        window.history.replaceState({}, document.title);
      } else {
        setShowTemplateModal(true);
      }
    } else {
      loadPage();
    }
  }, [slug, location.state]);

  const loadAllWorkspacePages = async () => {
    if (!currentWorkspace?.id) return;

    try {
      // Load pages for navigation/sidebar
      await pageApi.getAll(currentWorkspace.id);

      // Load all components from all pages (including subpages)
      const allComps = await pageApi.getAllComponents(currentWorkspace.id);

      setAllWorkspaceComponents(allComps);

      // Carrega as integrações no store global — sem isso, getIntegrationById()
      // só resolvia depois de abrir a config de algum componente, deixando
      // tabelas via Google Sheets sem sincronizar num builder recém-aberto.
      const allIntegrations = await integrationsApi.getAll(currentWorkspace.id);
      setIntegrationsStore(allIntegrations);
    } catch (error) {
      console.error("Failed to load workspace pages:", error);
    }
  };

  const loadPage = async () => {
    if (!slug || !currentWorkspace?.id) return;

    setIsLoading(true);
    try {
      const page = await pageApi.getOne(currentWorkspace.id, slug);
      setPageName(page.name);
      setPageSlug(page.slug);
      setPageDescription(page.description || "");
      setPageIcon(page.icon || "📄");
      setIsLocked((page.permissions as any)?.isLocked ?? false);
      setComponents(page.components || []);
    } catch (error) {
      console.error("Failed to load page:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddComponent = (component: PageComponent) => {
    // Validação: permitir apenas um filtro de data por página
    if (component.type === 'filter' && component.config.preset !== 'custom') {
      const existingDateFilter = components.find((c) => c.type === 'filter' && c.config.preset !== 'custom');
      if (existingDateFilter) {
        alert('Já existe um filtro de data nesta página. Apenas um filtro de data é permitido por página.');
        return;
      }
    }
    // Filtros customizados podem ter vários
    setComponents([...components, component]);
  };

  const handleTemplateSelect = (templateComponents: any[]) => {
    setComponents(templateComponents);
    setShowTemplateModal(false);
  };

  const handleEditComponent = (component: PageComponent) => {
    setEditingComponent(component);
  };

  const handleSaveEdit = (updatedComponent: PageComponent) => {
    setComponents(
      updateComponentDeep(components, updatedComponent.id, () => updatedComponent),
    );
  };

  const handleDataEdit = (componentId: string, newData: any[]) => {
    setComponents(
      updateComponentDeep(components, componentId, (c) => {
        // Para kanban, salvamos os dados em columns
        if (c.type === "kanban") {
          return { ...c, config: { ...c.config, columns: newData } };
        }

        // Para metric-card, salvamos o valor diretamente no config
        if (c.type === "metric-card") {
          return { ...c, config: { ...c.config, value: newData[0]?.value || 0 } };
        }

        // Para outros componentes, salvamos em dataSource.data
        return {
          ...c,
          config: {
            ...c.config,
            dataSource: {
              type: "static",
              ...c.config.dataSource,
              data: newData,
            },
          },
        };
      }),
    );
  };

  const handleColumnsChange = (componentId: string, columns: any[]) => {
    setComponents(
      updateComponentDeep(components, componentId, (c) => ({
        ...c,
        config: { ...c.config, columns },
      })),
    );
  };

  const handleChartFieldsChange = (
    componentId: string,
    xKey: string,
    yKey: string,
    groupDuplicates: boolean,
    limit?: number,
    sortOrder?: "asc" | "desc",
  ) => {
    setComponents(
      updateComponentDeep(components, componentId, (c) => ({
        ...c,
        config: {
          ...c.config,
          xKey,
          yKey,
          groupDuplicates,
          limit,
          sortOrder,
        },
      })),
    );
  };

  // Resolve uma amostra de componente fonte para o ChartFieldsModal listar campos
  // disponíveis quando o gráfico usa fonte dinâmica (via filtro "Tabela Vinculada").
  const resolveChartSampleSourceId = (config: any): string | undefined => {
    if (!config?.dynamicSourceField) return config?.sourceComponentId;
    const filterComp = components.find(
      (c) => c.type === "filter" && c.config?.field === config.dynamicSourceField,
    );
    return filterComp?.config?.options?.[0]?.value;
  };

  const handleKanbanColumnsChange = (componentId: string, columns: any[]) => {
    setComponents(
      updateComponentDeep(components, componentId, (c) => ({
        ...c,
        config: { ...c.config, columns },
      })),
    );
  };

  const handleComponentChange = (componentId: string, newConfig: any) => {
    setComponents(
      updateComponentDeep(components, componentId, (c) => ({
        ...c,
        config: newConfig,
      })),
    );
  };

  const handleDeleteComponent = (id: string) => {
    setComponents(removeComponentDeep(components, id));
  };

  const handleAddChild = (containerId: string, child: PageComponent) => {
    setComponents(addChildToContainer(components, containerId, child));
  };

  const handleMoveComponent = (componentId: string, targetContainerId: string) => {
    setComponents(
      moveComponentIntoContainer(components, componentId, targetContainerId),
    );
  };

  const handleMoveToRoot = (componentId: string) => {
    setComponents(moveComponentToRoot(components, componentId));
  };

  const handleFormSubmit = (data: any) => {
    if (!activeFormComponentId) return;

    const buttonComponent = components.find(
      (c) => c.id === activeFormComponentId,
    );
    if (!buttonComponent?.config.sourceComponentId) return;

    // Encontrar o componente fonte (tabela/lista)
    const sourceComponent = components.find(
      (c) => c.id === buttonComponent.config.sourceComponentId,
    );
    if (!sourceComponent) return;

    // Adicionar novo registro aos dados do componente fonte
    const currentData = sourceComponent.config.dataSource?.data || [];

    // Se for lista, adicionar ID automático
    const newData =
      sourceComponent.type === "list"
        ? {
            ...data,
            id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          }
        : data;

    const updatedData = [...currentData, newData];

    setComponents(
      components.map((c) =>
        c.id === sourceComponent.id
          ? {
              ...c,
              config: {
                ...c.config,
                dataSource: {
                  ...c.config.dataSource,
                  type: "static",
                  data: updatedData,
                },
              },
            }
          : c,
      ),
    );
  };

  const getFormColumns = () => {
    if (!activeFormComponentId) return [];

    const buttonComponent = components.find(
      (c) => c.id === activeFormComponentId,
    );
    if (!buttonComponent?.config.sourceComponentId) return [];

    // Buscar primeiro nos componentes locais
    let sourceComponent = components.find(
      (c) => c.id === buttonComponent.config.sourceComponentId,
    );

    // Se não encontrou localmente, buscar nos componentes de outras páginas
    if (!sourceComponent) {
      sourceComponent = allWorkspaceComponents.find(
        (c) => c.id === buttonComponent.config.sourceComponentId,
      );
    }

    if (!sourceComponent) return [];

    // Se for tabela, usar as colunas definidas (excluindo campos de ID)
    if (sourceComponent.type === "table" && sourceComponent.config.columns) {
      return sourceComponent.config.columns
        .filter((col: any) => !["id", "_id", "ID"].includes(col.field))
        .map((col: any) => ({
          field: col.field,
          label: col.label,
          type: col.type,
        }));
    }

    // Se for lista, usar campos padrão do ListWidget
    if (sourceComponent.type === "list") {
      return [
        { field: "title", label: "Título", type: "text" },
        { field: "subtitle", label: "Subtítulo", type: "text" },
        { field: "icon", label: "Ícone (emoji)", type: "text" },
        { field: "badge", label: "Badge", type: "text" },
      ];
    }

    return [];
  };

  const handleLayoutChange = (layout: Layout[]) => {
    const updatedComponents = components.map((component) => {
      const layoutItem = layout.find((l) => l.i === component.id);
      if (layoutItem) {
        return {
          ...component,
          position: {
            x: layoutItem.x,
            y: layoutItem.y,
            w: layoutItem.w,
            h: layoutItem.h,
          },
        };
      }
      return component;
    });
    setComponents(updatedComponents);
  };

  const generateSlug = (name: string): string => {
    return name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  };

  const handleSave = async () => {
    if (!currentWorkspace?.id) {
      setSaveError("Nenhum workspace selecionado");
      return;
    }

    if (!pageName.trim()) {
      setSaveError("Nome da página é obrigatório");
      return;
    }

    if (!pageSlug.trim()) {
      setSaveError("Slug (URL) é obrigatório");
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      const pageData = {
        name: pageName.trim(),
        slug: pageSlug.trim(),
        description: pageDescription.trim() || undefined,
        icon: pageIcon,
        components: components,
        layout: {},
      };

      if (!slug || slug === "new") {
        const newPage = await pageApi.create(currentWorkspace.id, pageData);
        navigate(`/pages/${newPage.slug}`);
      } else {
        await pageApi.update(currentWorkspace.id, slug, pageData);
        navigate(`/pages/${slug}`);
      }
    } catch (error: any) {
      console.error("Failed to save page:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Erro ao salvar página";
      setSaveError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="bg-background border-b px-6 py-4">
        <div className="flex items-center gap-4">
          <div className="flex gap-4 items-end">
            {/* Seletor de ícone */}
            <div className="space-y-2">
              <Label>Ícone</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-12 h-10 p-0 text-xl flex items-center justify-center"
                    title="Selecionar ícone"
                  >
                    <IconRenderer icon={pageIcon} className="w-5 h-5" />
                    {!ICON_MAP[pageIcon] && <span>{pageIcon}</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-2" align="start">
                  <p className="text-xs text-muted-foreground mb-2 px-1">Selecione um ícone</p>
                  <div className="grid grid-cols-6 gap-1">
                    {Object.keys(ICON_MAP).map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => setPageIcon(emoji)}
                        className={`w-9 h-9 flex items-center justify-center rounded-md hover:bg-accent transition-colors text-lg ${
                          pageIcon === emoji ? 'bg-primary/20 ring-1 ring-primary' : ''
                        }`}
                        title={emoji}
                      >
                        <IconRenderer icon={emoji} className="w-5 h-5 text-foreground" />
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label htmlFor="page-name">Nome da Página *</Label>
              <Input
                id="page-name"
                type="text"
                value={pageName}
                onChange={(e) => {
                  const newName = e.target.value;
                  setPageName(newName);
                  setPageSlug(generateSlug(newName));
                  setSaveError(null);
                }}
                className="w-64"
                placeholder="Ex: Dashboard Principal"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="page-description">Descrição</Label>
              <Input
                id="page-description"
                type="text"
                value={pageDescription}
                onChange={(e) => setPageDescription(e.target.value)}
                className="w-80"
                placeholder="Descreva o objetivo desta página..."
              />
            </div>
          </div>
          <div className="ml-auto flex items-center gap-4">
            {components.filter((c) => c.type === "filter").length > 0 && (
              <div className="border rounded-lg p-3 bg-muted/50">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-muted-foreground">Filtros:</span>
                  <div className="flex flex-wrap gap-2 ml-auto">
                    {components
                      .filter((c) => c.type === "filter")
                      .map((component) => (
                        <ComponentPreview
                          key={component.id}
                          component={component}
                          onEdit={handleEditComponent}
                          onDelete={handleDeleteComponent}
                          onDataEditClick={setDataEditingComponentId}
                          onColumnsEditClick={setColumnsEditingComponentId}
                          onChartFieldsEditClick={
                            setChartFieldsEditingComponentId
                          }
                          onKanbanColumnsEditClick={
                            setKanbanColumnsEditingComponentId
                          }
                          isBuilder={true}
                          allComponents={components}
                          isCompact={true}
                        />
                      ))}
                  </div>
                </div>
              </div>
            )}
            <div className="flex flex-col items-end gap-2">
              {saveError && (
                <div className="text-destructive text-sm">{saveError}</div>
              )}
              <div className="flex gap-2">
                {isSuperAdmin && slug && slug !== 'new' && (
                  <Button
                    variant="outline"
                    type="button"
                    onClick={async () => {
                      if (!currentWorkspace?.id) return;
                      const newLocked = !isLocked;
                      await pageApi.toggleLock(currentWorkspace.id, slug, newLocked);
                      setIsLocked(newLocked);
                    }}
                    title={isLocked ? 'Desbloquear edição' : 'Bloquear edição'}
                    className={isLocked ? 'border-destructive text-destructive hover:bg-destructive/10' : ''}
                  >
                    {isLocked ? <Lock className="w-4 h-4 mr-2" /> : <Unlock className="w-4 h-4 mr-2" />}
                    {isLocked ? 'Bloqueada' : 'Bloquear'}
                  </Button>
                )}
                <Button variant="outline" onClick={() => navigate("/")}>
                  <X className="w-4 h-4 mr-2" />
                  Cancelar
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={isSaving || !pageName.trim()}
                  title={
                    !pageName.trim()
                      ? "Preencha o nome da página"
                      : "Salvar página"
                  }
                >
                  <Save className="w-4 h-4 mr-2" />
                  {isSaving ? "Salvando..." : "Salvar"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Builder Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Component Palette */}
        <div className="w-80 bg-background border-r overflow-y-auto">
          <ComponentPalette onAddComponent={handleAddComponent} />
        </div>

        {/* Canvas */}
        <div className="flex-1 p-6 overflow-auto">
          <div className="bg-background rounded-lg shadow-sm min-h-full p-6">
            {components.length === 0 ? (
              <div className="text-center text-muted-foreground py-12">
                <p>Nenhum componente adicionado</p>
                <p className="text-sm mt-2">
                  Selecione um componente na paleta à esquerda
                </p>
              </div>
            ) : (
              <div
                ref={containerRef}
                className="w-full flex-1"
                style={{ maxWidth: "none", minHeight: "600px" }}
                onDragOver={(e) => {
                  if (e.dataTransfer.types.includes("application/x-component-id")) {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                  }
                }}
                onDrop={(e) => {
                  // Drops sobre um container são tratados lá (com stopPropagation).
                  // Aqui tratamos o "tirar para a página" (mover para a raiz).
                  const movingId = e.dataTransfer.getData(
                    "application/x-component-id",
                  );
                  if (movingId) {
                    e.preventDefault();
                    handleMoveToRoot(movingId);
                  }
                }}
              >
                <PageGrid
                  gridKey={slug || pageSlug || "new"}
                  components={components.filter((c) => c.type !== "filter")}
                  width={width}
                  height={height}
                  isDraggable={true}
                  isResizable={true}
                  onLayoutChange={handleLayoutChange}
                  draggableHandle=".drag-handle"
                  emptyState={
                    <div className="text-center text-muted-foreground py-12">
                      <p>Nenhum componente adicionado</p>
                      <p className="text-sm mt-2">
                        Selecione um componente na paleta à esquerda
                      </p>
                    </div>
                  }
                >
                  {(component) => (
                    <ComponentPreview
                      component={component}
                      onEdit={handleEditComponent}
                      onDelete={handleDeleteComponent}
                      onDataEditClick={setDataEditingComponentId}
                      onColumnsEditClick={setColumnsEditingComponentId}
                      onChartFieldsEditClick={setChartFieldsEditingComponentId}
                      onKanbanColumnsEditClick={
                        setKanbanColumnsEditingComponentId
                      }
                      onComponentChange={handleComponentChange}
                      onAddChild={handleAddChild}
                      onMoveComponent={handleMoveComponent}
                      isBuilder={true}
                      allComponents={components}
                    />
                  )}
                </PageGrid>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <ComponentEditModal
        component={editingComponent}
        onClose={() => setEditingComponent(null)}
        onSave={handleSaveEdit}
        availableComponents={
          allWorkspaceComponents.length > 0
            ? allWorkspaceComponents
            : components
        }
        pageComponents={components}
      />

      {/* Form Modal */}
      {activeFormComponentId && (
        <FormModal
          columns={getFormColumns()}
          onClose={() => setActiveFormComponentId(null)}
          onSubmit={handleFormSubmit}
        />
      )}

      {/* Data Edit Modal */}
      {dataEditingComponentId &&
        (() => {
          const component = findComponentDeep(
            components,
            dataEditingComponentId,
          );
          if (!component) return null;

          // Para kanban, os dados são as colunas; para metric-card, é o valor; para outros componentes, são os dados da dataSource
          let data;
          if (component.type === "kanban") {
            data = component.config.columns || [];
          } else if (component.type === "metric-card") {
            data = [{ value: component.config.value || 0 }];
          } else {
            data = component.config.dataSource?.data || [];
          }

          // Para relationship-table, atualizar os dados das tabelas de origem
          let updatedConfig = component.config;
          if (component.type === "relationship-table") {
            // Buscar dados atualizados das tabelas de origem
            if (component.config.leftTable?.id) {
              const leftTableComponent = components.find(
                (c) => c.id === component.config.leftTable.id,
              );
              if (leftTableComponent) {
                updatedConfig = {
                  ...updatedConfig,
                  leftTable: {
                    ...updatedConfig.leftTable,
                    data: leftTableComponent.config?.dataSource?.data || [],
                  },
                };
              }
            }

            if (component.config.rightTable?.id) {
              const rightTableComponent = components.find(
                (c) => c.id === component.config.rightTable.id,
              );
              if (rightTableComponent) {
                updatedConfig = {
                  ...updatedConfig,
                  rightTable: {
                    ...updatedConfig.rightTable,
                    data: rightTableComponent.config?.dataSource?.data || [],
                  },
                };
              }
            }
          }

          return (
            <DataEditModal
              isOpen={true}
              onClose={() => setDataEditingComponentId(null)}
              onSave={(newData) => {
                handleDataEdit(dataEditingComponentId, newData);
                setDataEditingComponentId(null);
              }}
              data={data}
              componentType={component.type}
              columns={component.config.columns}
              config={updatedConfig}
              allComponents={components}
              title={`Editar Dados - ${
                component.config.title || component.type
              }`}
            />
          );
        })()}

      {/* Column Visibility Modal */}
      {columnsEditingComponentId &&
        (() => {
          const component = findComponentDeep(
            components,
            columnsEditingComponentId,
          );
          if (!component || component.type !== "table") return null;
          return (
            <ColumnConfigModal
              columns={component.config.columns || []}
              availableTables={components.filter(
                (c) => c.type === "table" && c.id !== columnsEditingComponentId,
              )}
              onClose={() => setColumnsEditingComponentId(null)}
              onSave={(columns) => {
                handleColumnsChange(columnsEditingComponentId, columns);
                setColumnsEditingComponentId(null);
              }}
            />
          );
        })()}

      {/* Chart Fields Modal */}
      {chartFieldsEditingComponentId &&
        (() => {
          const component = findComponentDeep(
            components,
            chartFieldsEditingComponentId,
          );
          if (!component || component.type !== "chart") return null;
          return (
            <ChartFieldsModal
              chartType={component.config.chartType || "line"}
              data={component.config.dataSource?.data || []}
              currentXKey={component.config.xKey}
              currentYKey={component.config.yKey}
              sourceComponentId={resolveChartSampleSourceId(component.config)}
              allComponents={
                allWorkspaceComponents.length > 0
                  ? allWorkspaceComponents
                  : components
              }
              groupDuplicates={component.config.groupDuplicates}
              limit={component.config.limit}
              sortOrder={component.config.sortOrder}
              onClose={() => setChartFieldsEditingComponentId(null)}
              onSave={(xKey, yKey, groupDuplicates, limit, sortOrder) => {
                handleChartFieldsChange(
                  chartFieldsEditingComponentId,
                  xKey,
                  yKey,
                  groupDuplicates,
                  limit,
                  sortOrder,
                );
                setChartFieldsEditingComponentId(null);
              }}
            />
          );
        })()}

      {/* Kanban Columns Modal */}
      {kanbanColumnsEditingComponentId &&
        (() => {
          const component = findComponentDeep(
            components,
            kanbanColumnsEditingComponentId,
          );
          if (!component) return null;

          return (
            <KanbanColumnsModal
              isOpen={true}
              columns={component.config.columns || []}
              onClose={() => setKanbanColumnsEditingComponentId(null)}
              onSave={(columns) => {
                handleKanbanColumnsChange(
                  kanbanColumnsEditingComponentId,
                  columns,
                );
                setKanbanColumnsEditingComponentId(null);
              }}
              title={`Editar Colunas - ${component.config.title || "Kanban"}`}
            />
          );
        })()}

      {/* Template Selection Modal */}
      <PageTemplateModal
        isOpen={showTemplateModal}
        onClose={() => {
          setShowTemplateModal(false);
          if (!slug || slug === "new") {
            navigate("/");
          }
        }}
        onSelect={handleTemplateSelect}
      />
    </div>
  );
}
